import React, { useEffect, useState, useMemo } from 'react';
import apiClient, { formatApiError } from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import * as PhosphorIcons from '@phosphor-icons/react';

const { Plus, Trash, CaretLeft, CaretRight } = PhosphorIcons;

const Calendar = () => {
  const [occasions, setOccasions] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [groups, setGroups] = useState([]);
  const [formData, setFormData] = useState({ name: '', date: '', playlist_id: '', description: '', group_ids: [] });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [o, p, g] = await Promise.all([apiClient.get('/special-occasions'), apiClient.get('/playlists'), apiClient.get('/store-groups')]);
      setOccasions(o.data);
      setPlaylists(p.data);
      setGroups(g.data);
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/special-occasions', formData);
      toast.success('Occasion created — kiosks updated automatically');
      setOpen(false);
      setFormData({ name: '', date: '', playlist_id: '', description: '', group_ids: [] });
      fetchData();
    } catch (error) { toast.error(formatApiError(error)); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this occasion?')) return;
    try {
      await apiClient.delete(`/special-occasions/${id}`);
      toast.success('Occasion deleted — kiosks updated automatically');
      fetchData();
    } catch (error) { toast.error(formatApiError(error)); }
  };

  const getPlaylistName = (id) => playlists.find(p => p.id === id)?.name || 'Unknown';

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const monthName = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const calendarDays = useMemo(() => {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days = [];
    for (let i = 0; i < (firstDay === 0 ? 6 : firstDay - 1); i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(d);
    return days;
  }, [year, month]);

  const getOccasionsForDay = (day) => {
    if (!day) return [];
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return occasions.filter(o => o.date === dateStr);
  };

  const today = new Date();
  const isToday = (day) => day && today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));

  if (loading) return <div className="text-[#6b665c] font-mono">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-2" data-testid="calendar-title">Calendar</h1>
          <p className="text-[#6b665c] font-mono text-sm tracking-wider uppercase">Special occasions &amp; schedule overrides</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white border-0" data-testid="add-occasion-button">
              <Plus size={20} className="mr-2" />Add Occasion
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white border-[#c5d4c8] text-[#2d2a26]">
            <DialogHeader><DialogTitle className="font-mono">Create Special Occasion</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="add-occasion-form">
              <div>
                <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f]">Name</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]" placeholder="e.g., Diwali, Christmas" required data-testid="occasion-name-input" />
              </div>
              <div>
                <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f]">Date</Label>
                <Input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]" required data-testid="occasion-date-input" />
              </div>
              <div>
                <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f]">Playlist</Label>
                <Select value={formData.playlist_id} onValueChange={(v) => setFormData({ ...formData, playlist_id: v })}>
                  <SelectTrigger className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]" data-testid="occasion-playlist-select"><SelectValue placeholder="Select playlist" /></SelectTrigger>
                  <SelectContent className="bg-white border-[#c5d4c8] text-[#2d2a26]">
                    {playlists.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f]">Description</Label>
                <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]" rows={2} data-testid="occasion-description-input" />
              </div>
              {groups.length > 0 && (
                <div>
                  <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f] mb-2 block">Target Groups (empty = all stores)</Label>
                  <div className="space-y-2 bg-[#f5f0e8] border border-[#c5d4c8] rounded-md p-3 max-h-32 overflow-y-auto">
                    {groups.map(g => (
                      <div key={g.id} className="flex items-center gap-2">
                        <input type="checkbox" id={`occ-group-${g.id}`} checked={(formData.group_ids || []).includes(g.id)}
                          onChange={() => setFormData(prev => ({ ...prev, group_ids: (prev.group_ids || []).includes(g.id) ? prev.group_ids.filter(id => id !== g.id) : [...(prev.group_ids || []), g.id] }))}
                          className="rounded border-[#c5d4c8]" />
                        <label htmlFor={`occ-group-${g.id}`} className="text-sm cursor-pointer">{g.name}</label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white border-0" data-testid="submit-occasion-button">Create Occasion</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Calendar Grid */}
      <div className="bg-white border border-[#c5d4c8] rounded-xl overflow-hidden">
        {/* Month Navigation */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#c5d4c8]">
          <Button onClick={prevMonth} className="bg-[#e4ebe5] hover:bg-[#d9d3c5] text-[#2d2a26] border-0 w-9 h-9 p-0"><CaretLeft size={18} /></Button>
          <h2 className="text-lg font-bold font-mono">{monthName}</h2>
          <Button onClick={nextMonth} className="bg-[#e4ebe5] hover:bg-[#d9d3c5] text-[#2d2a26] border-0 w-9 h-9 p-0"><CaretRight size={18} /></Button>
        </div>

        {/* Day Headers */}
        <div className="grid grid-cols-7 border-b border-[#c5d4c8]">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
            <div key={d} className="text-center py-2 text-xs text-[#8a8477] font-mono uppercase tracking-wider">{d}</div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7">
          {calendarDays.map((day, i) => {
            const dayOccasions = getOccasionsForDay(day);
            // Stable key: YYYY-MM-DD for real days, padding cells use a
            // leading-"pad-" prefix so React still gets a unique key per cell
            // and rerenders don't misalign if the month/year changes.
            const cellKey = day
              ? `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
              : `pad-${year}-${month}-${i}`;
            return (
              <div key={cellKey} className={`min-h-[100px] border-b border-r border-[#c5d4c8]/50 p-2 ${!day ? 'bg-[#f5f0e8]/50' : 'hover:bg-[#e4ebe5]/30'} ${isToday(day) ? 'bg-blue-900/10' : ''}`}
                data-testid={day ? `calendar-day-${day}` : undefined}
                onClick={() => { if (day) { const ds = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`; setFormData(f => ({...f, date: ds})); setOpen(true); }}}
              >
                {day && (
                  <>
                    <span className={`text-sm font-mono ${isToday(day) ? 'text-[#7c9082] font-bold' : 'text-[#6b665c]'}`}>{day}</span>
                    {dayOccasions.map(occ => (
                      <div key={occ.id} className="mt-1 bg-blue-900/30 border border-[#c5d4c8] rounded px-1.5 py-1 group cursor-pointer" data-testid={`occasion-badge-${occ.id}`}>
                        <div className="flex items-center justify-between">
                          <p className="text-[10px] text-[#5a6e5f] font-medium truncate">{occ.name}</p>
                          <button onClick={(e) => { e.stopPropagation(); handleDelete(occ.id); }} className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 ml-1">
                            <Trash size={10} />
                          </button>
                        </div>
                        <p className="text-[9px] text-[#8a8477] truncate">{getPlaylistName(occ.playlist_id)}</p>
                      </div>
                    ))}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Calendar;
